import {
  isMergeJoin,
  isConditionOrGoalSplit,
  isGoalOrCondition,
  isLoopStep,
  isLoopStepNext,
} from 'pages/AutomationEngine/store/ts-utilities';
import type { Step } from 'pages/AutomationEngine/types';
import type { ExecutionHistoryStep } from './types';

// A skipped loop never runs its body, so every step between the loop and the
// Next/Finish that closes it is unreachable.
const isSkippedLoop = (parentStep?: Partial<Step>) =>
  Boolean(
    parentStep && isLoopStep(parentStep) && parentStep.shouldSkipExecution
  );

// Whether a step can never be reached in this execution, because an ancestor
// was skipped onto another path, archived the record, or is a skipped loop.
//
// Note the tree here is the one the flow renders: a Next/Finish hangs off the
// end of its loop's body, and the steps that run after the loop hang off the
// Next/Finish. Climbing out of a loop therefore has to jump the whole body,
// which is what the Next/Finish case below does.
export const isStepUnreachable = (
  step: ExecutionHistoryStep,
  steps: Step[]
): boolean => {
  if (step.shouldSkipExecution || !step.parentKey) {
    return false;
  }
  const stepsByIds = steps.reduce<Record<string, Partial<Step>>>(
    (acc, step) => {
      acc[step.id] = step;
      return acc;
    },
    {}
  );

  const branchSkipLookup = steps
    .filter((s) => isGoalOrCondition(s))
    .reduce<Record<string, any>>((acc, branch) => {
      let continueWithBranch = '';
      if (branch.parentKey) {
        continueWithBranch = branch.continueWithBranch || '';

        let parentStep = stepsByIds[branch.parentKey];
        let childStep = branch;
        while (parentStep) {
          if (isMergeJoin(parentStep)) {
            if (!parentStep.parentKey) {
              break;
            }

            // skip pass merge joins to infront of the split
            const split = stepsByIds[parentStep.parentKey];
            if (!split.parentKey) {
              break;
            }

            childStep = parentStep as ExecutionHistoryStep;
            parentStep = stepsByIds[split.parentKey];
            continue;
          }

          if (isLoopStepNext(parentStep)) {
            // climbing out from after a loop: jump the body, which did not
            // run on the way to this step
            const loopStep = stepsByIds[parentStep.loopParent || ''];
            if (!loopStep?.parentKey) {
              break;
            }
            childStep = loopStep as ExecutionHistoryStep;
            parentStep = stepsByIds[loopStep.parentKey];
            continue;
          }

          if (isSkippedLoop(parentStep)) {
            // the branch sits in a loop that never runs, so neither of its
            // paths can be reached
            continueWithBranch = 'both';
            break;
          }

          if (parentStep.shouldSkipExecution) {
            continueWithBranch = parentStep.continueWithBranch || '';
          } else if (
            isConditionOrGoalSplit(parentStep) &&
            parentStep.parentKey
          ) {
            // need to look at the branch parent
            const branchParent = stepsByIds[parentStep.parentKey];
            if (branchParent.shouldSkipExecution) {
              const unreachable = Boolean(
                branchParent.continueWithBranch &&
                  branchParent.continueWithBranch !==
                    (childStep.parentCondition || childStep.fromLabel)
              );
              if (unreachable) {
                continueWithBranch = 'both';
              }
            }
            break;
          }

          if (!parentStep.parentKey) {
            break;
          }

          childStep = parentStep as ExecutionHistoryStep;
          parentStep = stepsByIds[parentStep.parentKey];
        }
      } else {
        // at the route
        continueWithBranch = branch.continueWithBranch || '';
      }
      acc[branch.id] = continueWithBranch;
      return acc;
    }, {});

  let parentStep = stepsByIds[step.parentKey];
  let childStep = step;
  let unreachable = false;

  while (parentStep) {
    {
      if (isMergeJoin(parentStep)) {
        childStep = parentStep as ExecutionHistoryStep;
        if (!parentStep.parentKey) {
          break;
        }
        // skip pass merge joins to infront of the split
        const split = stepsByIds[parentStep.parentKey];
        if (!split.parentKey) {
          break;
        }
        parentStep = stepsByIds[split.parentKey];
        continue;
      }

      if (isLoopStepNext(parentStep)) {
        // climbing out from after a loop: jump the body, which did not run
        // on the way to this step
        const loopStep = stepsByIds[parentStep.loopParent || ''];
        if (!loopStep?.parentKey) {
          break;
        }
        childStep = loopStep as ExecutionHistoryStep;
        parentStep = stepsByIds[loopStep.parentKey];
        continue;
      }

      if (isSkippedLoop(parentStep)) {
        unreachable = true;
        break;
      }

      if (parentStep.shouldSkipExecution) {
        unreachable = Boolean(
          parentStep.continueWithBranch &&
            parentStep.continueWithBranch !==
              (childStep.parentCondition || childStep.fromLabel)
        );
      } else if (isConditionOrGoalSplit(parentStep) && parentStep.parentKey) {
        // need to look at the branch parent
        const branchParent = stepsByIds[parentStep.parentKey];

        if (branchParent?.id && branchSkipLookup[branchParent?.id] === 'both') {
          unreachable = true;
          break;
        }

        if (branchParent.shouldSkipExecution) {
          unreachable = Boolean(
            branchParent.continueWithBranch &&
              branchParent.continueWithBranch !==
                (childStep.parentCondition || childStep.fromLabel)
          );
        }
        break;
      } else if (parentStep.type === 'archive_record') {
        unreachable = Boolean(
          parentStep.details?.recordSource === 'this_record'
        );
      }

      if (unreachable) {
        break;
      }

      if (!parentStep.parentKey) {
        break;
      }
      childStep = parentStep as ExecutionHistoryStep;
      parentStep = stepsByIds[parentStep.parentKey];
    }
  }

  return unreachable;
};
